import React, { useContext, useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FormSpy } from 'react-final-form';
import { OnChange } from "react-final-form-listeners";

import { setSelectedBaby } from '../../actions/ui';
import { formatProfiles } from "./helpers";
import {
  FormLabel,
  FormSelect,
  FormRadio,
  FormIconChooser,
  BaseForm,
  FormTextArea,
  FormDateInput,
  FormInputHolder
} from "../form";
import { PrimaryButton, SecondaryButton } from "../buttons";
import babyIcons from "../icons/babyIcons";
import ErrorMessage from "../ErrorMessage/ErrorMessage";

import { required } from "../../helpers/formValidators";
import { Error } from "../form/BaseForm";
import { FormContext } from "../../context/form-context";

export const MILESTONE_OPTIONS = [
  { labelKey: "milestones.openedEyes", defaultLabel: "I opened my eyes for the first time", value: "opened-eyes-first-time" },
  { labelKey: "milestones.gainedWeight", defaultLabel: "Today I gained some weight", value: "gained-weight" },
  { labelKey: "milestones.firstCuddle", defaultLabel: "First cuddle", value: "first-cuddle" },
  { labelKey: "milestones.wireFree", defaultLabel: "Wire free!", value: "wire-free" },
  { labelKey: "milestones.smiled", defaultLabel: "I smiled for the first time", value: "smiled-first-time" },
  { labelKey: "milestones.firstSelfie", defaultLabel: "First Selfie", value: "first-selfie" },
  { labelKey: "milestones.latched", defaultLabel: "I latched today", value: "latched-today" },
  { labelKey: "milestones.skinSuperstar", defaultLabel: "Skin-to-skin superstar", value: "skin-superstar" },
  { labelKey: "milestones.firstBath", defaultLabel: "First bath", value: "first-bath" },
  { labelKey: "milestones.diaperChanged", defaultLabel: "First diaper changed", value: "first-diaper-changed" },
  { labelKey: "milestones.firstCot", defaultLabel: "First time in cot or crib", value: "first-cot-crib" },
  { labelKey: "milestones.firstSkinContact", defaultLabel: "First time in skin to skin contact", value: "skin-skin-contact" },
  { labelKey: "milestones.holdingClothed", defaultLabel: "First holding baby clothed", value: "holding-baby-clothing" },
  { labelKey: "milestones.firstFeed", defaultLabel: "First feed", value: "first-feed" },
  { labelKey: "milestones.firstBreastfeed", defaultLabel: "First breastfeed", value: "first-breastfeed" },
  { labelKey: "milestones.firstSong", defaultLabel: "First song", value: "first-song" },
  { labelKey: "milestones.firstBook", defaultLabel: "First book read", value: "first-book-read" },
];

const MilestoneForm = ({
  babies = [],
  profiles = [],
  selectedProfile = {},
  selectedBabyId = null,
  showFirstTimeSavingModal = false,
  showUnSavedModal = () => {},
  showSaveEntryModal = () => {},
  milestoneRecordsPost = () => Promise.resolve(),
  milestoneRecordsPatch = () => Promise.resolve(),
  milestoneRecordsList = () => Promise.resolve(), 
  hideTrackerModals = () => {},
  handleRecords = () => {},
  accountId,
  milestoneModalShown = false
}) => {
  const { t } = useTranslation();
  const [initialValues, setInitialValues] = useState(null);
  const formContext = useContext(FormContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const milestoneList = useMemo(() => 
    MILESTONE_OPTIONS.map(m => ({
      label: t(m.labelKey, m.defaultLabel),
      value: m.value
    })), [t]);

  const defaultValues = {
    babyId: selectedBabyId, 
    providerId: selectedProfile?.id,
    date: new Date()
  };

  useEffect(() => {
    if (!milestoneModalShown) {
      setInitialValues(null);
      return;
    }

    if (formContext.formValues && Object.keys(formContext.formValues).length > 0) {
      setInitialValues({ ...formContext.formValues });
      return;
    }

    const latestRecord = handleRecords('milestones', selectedBabyId) || {};
    setInitialValues({
      ...defaultValues,
      ...latestRecord,
      babyId: selectedBabyId 
    });
  }, [milestoneModalShown, selectedBabyId]); 

  const toggleWarningModal = () => {
    return formContext.isDirty ? showUnSavedModal() : hideTrackerModals();
  };

  const onSubmit = (values) => {
    const recordBody = {
      ...values,
      providerId: values.providerId === "other" ? null : values.providerId,
      otherProvider: values.providerId !== "other" ? null : values.otherProvider
    };
    
    const originalBabyId = initialValues?.babyId;
    const newBabyId = values.babyId || originalBabyId;

    if (values.id) {
      return milestoneRecordsPatch(
        { accountId, babyId: originalBabyId, id: values.id },
        { body: JSON.stringify(recordBody) }
      )
        .then((data) => { 
          if (data) {
            milestoneRecordsList({ accountId, babyId: originalBabyId });
            if (newBabyId !== originalBabyId) {
              milestoneRecordsList({ accountId, babyId: newBabyId });
            }
            hideTrackerModals();
          }
        })
        .catch(() => <ErrorMessage />); 
    }

    return milestoneRecordsPost(
      { accountId, babyId: newBabyId },
      { body: JSON.stringify(recordBody) }
    )
      .then((data) => { 
        if (data) {
          milestoneRecordsList({ accountId, babyId: newBabyId });
          hideTrackerModals();
          dispatch(setSelectedBaby(newBabyId));
          navigate('/dashboard/milestones');
        }
      })
      .catch(() => <ErrorMessage />);
  };

  const onSave = (values) => {
    if (!showFirstTimeSavingModal) return onSubmit(values);
    formContext.setValues(values);
    formContext.setFunctions({ onSubmit });
    return showSaveEntryModal();
  };

  if (!milestoneModalShown || !initialValues) return null;

  return (
    <BaseForm 
      key={initialValues.id || 'new'}
      className="milestoneModal" 
      onSubmit={onSave} 
      initialValues={initialValues}
    >
      <h2 className="title">{t("tracker.trackMilestone")}</h2>

      {babies.length > 1 && !initialValues?.id && (
        <React.Fragment>
          <FormIconChooser>
            {babies.map((baby) => (
              <FormRadio key={baby.id} value={baby.id} name="babyId" imageSrc={babyIcons[baby.icon]} validate={required}>
                {baby.name}
              </FormRadio>
            ))}
          </FormIconChooser>
          <OnChange name="babyId">
            {(value, previous) => {
              if (value === previous) return;
              const latestRecord = handleRecords('milestones', value) || {};
              setInitialValues({ ...initialValues, ...latestRecord, babyId: value });
            }}
          </OnChange>
          <Error name="babyId" />
        </React.Fragment>
      )}

      {babies && initialValues?.id && (() => {
        const editBaby = babies.find(b => b.id === initialValues.babyId);
        return editBaby ? (
          <FormIconChooser>
            <label className="form-radio form-radio--checked">
              <div className="form-radio__image-container">
                <img src={babyIcons[editBaby.icon]} alt={editBaby.name} className="form-radio__image" width="2.4rem" height="2.4rem" />
              </div>
              <span className="form-radio__label">{editBaby.name}</span>
            </label>
          </FormIconChooser>
        ) : null;
      })()}

      <div>
        <FormLabel htmlFor="providerId">{t("tracker.provider", "Provider")}</FormLabel>
        <FormSelect name="providerId" options={formatProfiles(profiles)} validate={required} includeOther otherName="otherProvider" />
      </div>

      <div>
        <FormLabel>{t("tracker.chooseMilestone")}</FormLabel>
        <FormSelect placeholder={t("tracker.select", "Select")} name="milestoneType" options={milestoneList} includeOther validate={required} otherName="otherMilestone" />
      </div>

      <div>
        <FormTextArea
          maxLength={300}
          name="note"
          labelRenderer={() => <FormLabel note={t("global.optional", "Optional")}>{t("tracker.note", "Note")}</FormLabel>}
        />
      </div>

      <div>
        <FormLabel>{t("tracker.date")}</FormLabel>
        <FormDateInput name="date" validate={required} dateFormat="MMM, dd yyyy" />
      </div>

      <FormSpy subscription={{ submitting: true }}>
        {({ submitting }) => (
          <FormInputHolder>
            <SecondaryButton type="button" label={t("tracker.cancel", "Cancel")} onClick={toggleWarningModal} />
            <PrimaryButton type="submit" label={t("tracker.save", "Save")} disabled={submitting} />
          </FormInputHolder>
        )}
      </FormSpy>

      <FormSpy
        subscription={{ dirty: true, visited: true, values: true }}
        onChange={({ dirty, visited, values }) => {
          setTimeout(() => {
            if (dirty) {
              formContext.setValues(values);
              const actuallyDirty = Object.values(visited).some(Boolean);
              actuallyDirty ? formContext.setDirty() : formContext.notDirty();
            }
          }, 0);
        }}
      />
    </BaseForm>
  );
};

MilestoneForm.propTypes = {
  babies: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, name: PropTypes.string, icon: PropTypes.string })).isRequired,
  profiles: PropTypes.arrayOf(PropTypes.object),
  selectedProfile: PropTypes.shape({ id: PropTypes.string }),
  selectedBabyId: PropTypes.string,
  showFirstTimeSavingModal: PropTypes.bool,
  showUnSavedModal: PropTypes.func,
  showSaveEntryModal: PropTypes.func,
  milestoneRecordsPost: PropTypes.func,
  milestoneRecordsPatch: PropTypes.func,
  milestoneRecordsList: PropTypes.func, 
  hideTrackerModals: PropTypes.func,
  handleRecords: PropTypes.func,
  accountId: PropTypes.string,
  milestoneModalShown: PropTypes.bool
};

export default MilestoneForm;